// src/services/earnings-api.ts
import { isPanelMasked, PRIVACY_MASK } from '../utils/format';
// ============================================================================
// KAZANÇ API SERVİSİ
// ============================================================================
// Bu dosya EarningsController ve AdminEarningsController için API çağrılarını içerir.
// Mevcut api.ts dosyasına import edilebilir veya ayrı kullanılabilir.
// ============================================================================

import { apiService } from './api';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface EarningPeriod {
  id?: string;
  year: number;
  month: number;
  periodName: string;
  periodStartDate?: string;
  periodEndDate?: string;
  totalOrderAmount?: number;
  // Kazanç detayları
  totalEarnings: number;
  verifiedEarnings: number;
  unverifiedEarnings: number;
  // Sipariş sayıları
  deliveredOrderCount: number;
  verifiedOrderCount: number;
  unverifiedOrderCount: number;
  totalItemCount?: number;
  // Ödeme penceresi
  paymentWindowStart: string;
  paymentWindowEnd: string;
  paymentWindowText?: string;
  // Durum
  status: 'InProgress' | 'AwaitingPayment' | 'PaymentPending' | 'Paid' | 'CarriedOver';
  statusDescription: string;
  statusColor?: string;
  paidAtUtc?: string;
  transactionReference?: string;
  paymentNote?: string;
  // Devir — asgari ödeme tutarının altında kalıp sonraki döneme ertelenen dönemler
  carriedToYear?: number;
  carriedToMonth?: number;
  carriedToPeriodName?: string;
  carryIns?: CarryInItem[];
  carryInAmount?: number;
  /** Bu dönemde fiilen ödenecek tutar = kendi doğrulanmış + devirler */
  payableAmount?: number;
  // Doğrulama uyarısı
  hasUnverifiedOrders: boolean;
  unverifiedWarning?: string;
}

/** Bir döneme başka dönemden devirle gelen kalem */
export interface CarryInItem {
  year: number;
  month: number;
  periodName: string;
  amount: number;
}

export interface EarningsSummary {
  totalAllTimeEarnings: number;
  totalVerifiedEarnings: number;
  totalPaidEarnings: number;
  totalPendingEarnings: number;      // Sadece doğrulanmış
  totalUnverifiedEarnings: number;
  currentMonthEarnings: number;
  totalDeliveredOrders: number;
  completedPeriods: number;
  pendingPayments: number;
  /** Sonraki döneme devredilen (asgari tutar altında kalan) toplam — bekleyene DAHİLDİR */
  totalCarriedOverEarnings?: number;
  carriedOverPeriods?: number;
  /** Asgari ödeme tutarı — devrin sebebini kullanıcıya yazmak için */
  minPayoutAmount?: number;
  nextPaymentDate?: string;
  nextPaymentAmount?: number;        // Sadece doğrulanmış
}

export interface UserEarningsResponse {
  summary: EarningsSummary;
  periods: EarningPeriod[];
}

export interface PeriodOrder {
  id: string;
  orderId: string;
  orderDateUtc: string;
  total: number;
  status: string;
  earnings: number;
  itemCount: number;
  updatedAtUtc: string;
  // Doğrulama durumu
  isVerified: boolean;
  verificationRequiredDate: string;
}

export interface PeriodDetailResponse {
  period: EarningPeriod;
  orders: PeriodOrder[];
}

// Admin Types
export interface PendingPeriod {
  year: number;
  month: number;
  periodName: string;
  // Toplam
  totalEarnings: number;
  totalOrderCount: number;
  // Doğrulanmış (ödenebilir)
  verifiedEarnings: number;
  verifiedOrderCount: number;
  // Doğrulanmamış (ödenemez)
  unverifiedEarnings: number;
  unverifiedOrderCount: number;
  // DEVİR — önceki dönemlerden taşınan tutar
  carryInAmount: number;
  carryIns: Array<{
    year: number;
    month: number;
    periodName: string;
    verifiedEarnings: number;
    verifiedOrderCount: number;
  }>;
  /** Bu dönemde fiilen ödenecek tutar = kendi doğrulanmış + devirler */
  payableAmount: number;
  minPayoutAmount: number;
  /** Asgari tutarın altında → "sonraki döneme devret" butonu gösterilir */
  isBelowMinimum: boolean;
  // Diğer
  paymentWindowStart: string;
  /** Ödeme penceresi açıldı mı — false ise dönem "sıradaki" olarak gösterilir */
  paymentWindowOpen?: boolean;
  hasUnverifiedOrders: boolean;
}

export interface PaymentMethod {
  currency: string;
  accountHolderName?: string;
  maskedAccount?: string;
}

export interface PendingUser {
  userId: string;
  fullName: string;
  email: string;
  // Toplam bekleyen
  totalPendingAmount: number;
  // Doğrulanmış (ödenebilir)
  verifiedPendingAmount: number;
  /** Ödeme tarihi henüz gelmemiş dönemlerin toplamı (sırada) */
  upcomingPendingAmount?: number;
  // Doğrulanmamış (ödenemez)
  unverifiedPendingAmount: number;
  pendingPeriodCount: number;
  pendingPeriods: PendingPeriod[];
  primaryPaymentMethod?: PaymentMethod;
  hasUnverifiedOrders: boolean;
}

export interface AdminPendingResponse {
  // Toplam bekleyen
  totalPendingAmount: number;
  // Doğrulanmış (ödenebilir)
  totalVerifiedAmount: number;
  /** Ödeme tarihi henüz gelmemiş dönemlerin toplamı (sırada) */
  totalUpcomingAmount?: number;
  // Doğrulanmamış (ödenemez)
  totalUnverifiedAmount: number;
  totalPendingPeriods: number;
  totalUsersWithPending: number;
  users: PendingUser[];
}

export interface AdminUserPeriodsResponse {
  user: {
    userId: string;
    fullName: string;
    email: string;
    memberSince: string;
  };
  primaryPaymentMethod?: PaymentMethod & { paymentProvider?: string };
  periods: EarningPeriod[];
}

export interface PaymentHistoryItem {
  id: string;
  userId: string;
  userFullName: string;
  userEmail: string;
  year: number;
  month: number;
  periodName: string;
  /** Bankadan çıkan toplam (ana dönem + devirle kapanan dönemler) */
  paidAmount: number;
  /** Ana dönemin kendi tutarı */
  ownAmount?: number;
  carryInAmount?: number;
  carriedPeriods?: Array<{
    id: string;
    year: number;
    month: number;
    periodName: string;
    paidAmount: number;
    paidOrderCount?: number;
  }>;
  /** Bu transferle kapanan dönem sayısı (1 = devir yok) */
  periodCount?: number;
  paidOrderCount: number;
  paidAtUtc: string;
  transactionReference?: string;
  paymentNote?: string;
  approvedByAdminId?: string;
}

export interface PaymentHistoryResponse {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  payments: PaymentHistoryItem[];
}

export interface ApprovePaymentRequest {
  transactionReference?: string;
  paymentNote?: string;
  /** Doğrulanmış tutarı $0 olan dönemi "$0 ödendi" olarak kapat */
  closeAtZero?: boolean;
}

export interface BulkApproveRequest {
  periods: Array<{
    userId: string;
    year: number;
    month: number;
  }>;
  transactionReference?: string;
  paymentNote?: string;
}

// ============================================================================
// USER EARNINGS API
// ============================================================================

/**
 * Kullanıcının kazanç özetini ve tüm dönemlerini getirir
 */
export async function getUserEarnings(): Promise<UserEarningsResponse> {
  return apiService.getUserEarnings();
}

export async function getPeriodDetail(year: number, month: number): Promise<PeriodDetailResponse> {
  return apiService.getPeriodDetail(year, month);
}

export async function getAdminPendingPayments(): Promise<AdminPendingResponse> {
  return apiService.getAdminPendingPayments();
}

export async function getAdminUserPeriods(userId: string): Promise<AdminUserPeriodsResponse> {
  return apiService.getAdminUserPeriods(userId);
}

export async function approvePayment(
  userId: string,
  year: number,
  month: number,
  request: ApprovePaymentRequest
): Promise<{ message: string; period: EarningPeriod }> {
  return apiService.approvePayment(userId, year, month, request);
}

export async function bulkApprovePayments(request: BulkApproveRequest): Promise<any> {
  return apiService.bulkApprovePayments(request);
}

export async function getPaymentHistory(
  page: number = 1,
  pageSize: number = 20
): Promise<PaymentHistoryResponse> {
  return apiService.getPaymentHistory(page, pageSize);
}

// ============================================================================
// DEVİR (CARRY-OVER) — asgari ödeme tutarının altında kalan dönemler
// ============================================================================

/** Dönemi bir sonraki döneme devreder (para taşınmaz, ödeme ertelenir) */
export async function carryOverPeriod(
  userId: string,
  year: number,
  month: number
): Promise<{ message: string; carriedToPeriodName: string; carriedAmount: number }> {
  return apiService.carryOverEarningPeriod(userId, year, month);
}

/** Yanlış yapılan devri geri alır */
export async function undoCarryOverPeriod(
  userId: string,
  year: number,
  month: number
): Promise<{ message: string }> {
  return apiService.undoCarryOverEarningPeriod(userId, year, month);
}

/** Asgari ödeme tutarı (tek genel eşik — kullanıcı kazancı + satış ortaklığı ortak) */
export async function getMinPayout(): Promise<{ minPayoutAmount: number }> {
  return apiService.getMinPayout();
}

export async function setMinPayout(amount: number): Promise<{ message: string; minPayoutAmount: number }> {
  return apiService.setMinPayout(amount);
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Durum rengini döndürür
 */
export function getStatusColor(status: EarningPeriod['status']): string {
  switch (status) {
    case 'InProgress':
      return '#3B82F6'; // blue
    case 'AwaitingPayment':
      return '#8B5CF6'; // purple
    case 'PaymentPending':
      return '#F59E0B'; // amber
    case 'Paid':
      return '#10B981'; // green
    case 'CarriedOver':
      return '#0EA5E9'; // sky — devredildi
    default:
      return '#6B7280'; // gray
  }
}

/**
 * Durum açıklamasını Türkçe döndürür
 */
export function getStatusText(status: EarningPeriod['status']): string {
  switch (status) {
    case 'InProgress':
      return 'Devam Ediyor';
    case 'AwaitingPayment':
      return 'Ödeme Tarihi Bekleniyor';
    case 'PaymentPending':
      return 'Ödeme Bekleniyor';
    case 'Paid':
      return 'Ödendi';
    case 'CarriedOver':
      return 'Sonraki Döneme Devredildi';
    default:
      return 'Bilinmiyor';
  }
}

/**
 * Para formatı
 */
export function formatCurrency(amount: number): string {
  // Gizlilik modu tek merkezden yönetilir (utils/format). Burada kendi
  // formatını üretirsek maske atlanır ve tutar sızar → delege ediyoruz.
  if (isPanelMasked()) return PRIVACY_MASK;
  return `$${amount.toFixed(2)}`;
}

/**
 * Tarih formatı (Türkçe)
 */
export function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Kısa tarih formatı
 */
export function formatShortDate(dateStr: string | undefined): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('tr-TR');
}